import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { LoggerService } from "@teammood/shared/util/logger";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { catchError, debounceTime, distinctUntilChanged, EMPTY, from, mergeMap, Subject, takeUntil, tap } from "rxjs";
import { isEqual } from "lodash";
import { TmWorkingDaysComponent, WorkingDays } from "@teammood/shared/ui/working-days";

import { TeamForAccount, TeamMemberForAccount } from "../../account/account.type";
import { AccountService } from "../../account/account.service";
import { AuthService } from "../../../auth/auth.service";
import { Router } from "@angular/router";

@Component({
    selector: "notification-days-settings",
    templateUrl: "./notification-days-settings.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    TmWorkingDaysComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule
]
})
export class NotificationDaysSettingsComponent implements OnInit, OnDestroy {

    teamMember: TeamMemberForAccount;

    form: FormGroup = new FormGroup(
        {
            workingDays: new FormArray([])
        }
    )

    private _destroy$ = new Subject<void>();

    get workingDays(): FormArray {
        return this.form.get('workingDays') as FormArray;
    }

    @Output()
    onTeamChange = new EventEmitter<TeamMemberForAccount>();

    constructor(
        private translate: TranslateService,
        private snackBarService: SnackBarService,
        private accountService: AccountService,
        private logger: LoggerService,
        private authService: AuthService,
        private router: Router,
    ) {
    }

    private updateWorkingDays(workingDays: WorkingDays[]) {
        const teams =
            this.teamMember.teams
                .map((team, index) => {
                    return {
                        ...team,
                        workingDays: workingDays[index]
                    }
                });
        this.teamMember = {
            ...this.teamMember,
            teams: teams,
        };
    }

    private async load() {
        this.teamMember = await this.accountService.get();
        this.workingDays.clear({emitEvent: false});
        this.teamMember.teams.forEach(team => {
            const fc = new FormControl();
            fc.setValue(team.workingDays, {emitEvent: false});
            this.workingDays.push(fc, {emitEvent: false});
            
        });
    }

    async ngOnInit() {
        await this.load();
        this.workingDays.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(async workingDays => { // array of workingDays
                this.updateWorkingDays(workingDays);
                await this.saveChanges();
            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    async saveChanges(reload: boolean = true) {
        try {
            await this.accountService.update(this.teamMember)
            this.snackBarService.success(this.translate.instant('saved'));
            this.onTeamChange.emit(this.teamMember);
            if(reload) {
                await this.load();
            }
        } catch (e) {
            this.logger.error(e)
            this.snackBarService.error(this.translate.instant('error'));
        }
    }

    workingDaysDifferentThanDefaults = (team: TeamForAccount) => {
        return !isEqual(team.workingDays, team.teamWorkingDays)
    }

    workingDaysDefault = async (team: TeamForAccount) => {
        team.workingDays = {...team.teamWorkingDays};
        await this.saveChanges();
    }

    async toggleTeam (teamId: string){
        const teams = this.teamMember.teams.map((team) => {
            if (team.teamId === teamId) {
                return {
                    ...team,
                    active: !team.active
                }
            }
            return team;
        })

        if(this.checkIfTeamMemberDisabled(teams)){
            // open confirm dialog to disable user or cancel
            await this.openDisableDialog();
        }else{
            // toggle team and save
            this.teamMember = {
                ...this.teamMember,
                teams: [...teams]
            }
            await this.saveChanges();
        }
    }

    checkIfTeamMemberDisabled (teams) {
        if (teams) {
            return !teams.reduce((a, team) => {
                return a || team.active === true;
            }, false);
        }
    }

    async openDisableDialog() {
        const deleteDialogRef = await this.accountService.disableUser();
    
        deleteDialogRef.beforeClosed().pipe(
            mergeMap((shouldDelete) => {
                if (shouldDelete) {
                    return from(this.disableTeamMember(this.teamMember))
                } else {
                    return EMPTY;
                }
                }),
            catchError(err =>{
                this.logger.error(err);
                return EMPTY;
            }),
          takeUntil(this._destroy$)
        ).subscribe()
      }

    async disableTeamMember (teamMember: TeamMemberForAccount) {
        if (teamMember && teamMember.teams) {
            teamMember.teams.forEach((team) => {
                team.active = false;
            });
            await this.saveChanges(false);
            // logout disabled user
            await this.authService.logout();
            await this.router.navigate(["/login"]);
        }
    }
    
}